<template>
  <div
    ref="sectionRef"
    class="dashboard-section"
    :class="{ 'focused': isFocused }"
    :style="{ '--section-index': sectionIndex }"
  >
    <div
      ref="sectionContentRef"
      class="section-content"
      :class="{ 'drop-zone-active': isDragOver, 'focused': isFocused, 'edit-mode': editMode }"
      @dragover.prevent="handleDragOver"
      @dragleave="handleDragLeave"
      @drop="handleDrop"
    >
      <div
        class="section-header-wrapper"
        :class="{ 'edit-mode': editMode }"
        @wheel.stop
        @touchmove.stop
      >
        <SectionHeader
          :title="section.title"
          :expanded="section.expanded"
          :display-mode="section.displayMode || 'detailed'"
          :edit-mode="editMode"
          :can-move-up="canMoveUp"
          :can-move-down="canMoveDown"
          @update:title="(title) => handleTitleUpdate(title)"
          @update:expanded="(expanded) => handleExpandedUpdate(expanded)"
          @update:display-mode="(displayMode) => handleDisplayModeUpdate(displayMode)"
          @delete="handleDelete"
          @move-up="handleMoveUp"
          @move-down="handleMoveDown"
        />
      </div>
      <div
        v-if="section.expanded"
        class="section-body"
      >
        <DashboardGrid
          v-if="section.widgets && section.widgets.length > 0"
          v-model="localWidgets"
          :columns="columns"
          :row-height="sectionRowHeight"
          :gap="gap"
          :edit-mode="editMode"
          :section-index="sectionIndex"
          :section-id="section.id"
          :is-drag-over="isDragOver"
          :focused="isFocused"
          @update:model-value="handleWidgetsUpdate"
          @open-settings="$emit('open-settings')"
          @remove-widget="handleRemoveWidget"
          @open-add-widget="handleOpenAddWidget"
        />
        <div
          v-else-if="editMode"
          class="empty-section-message"
          :class="{ 'drag-over': isDragOver }"
          @click="handleEmptySectionClick"
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            class="empty-section-icon"
          >
            <path
              d="M12 5V19M5 12H19"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
            />
          </svg>
          <p>Click to add widgets</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, computed, markRaw, nextTick } from 'vue'
import SectionHeader from './SectionHeader.vue'
import DashboardGrid from './DashboardGrid.vue'
import { useAutomationsFooter } from '../composables/useAutomationsFooter.js'
import { AUTOMATIONS_SECTION_WIDGET_TYPES } from '../utils/widgetBestPractices.js'

const props = defineProps({
  section: {
    type: Object,
    required: true
  },
  columns: {
    type: Number,
    default: 3
  },
  rowHeight: {
    type: Number,
    default: 360
  },
  gap: {
    type: Number,
    default: 20
  },
  editMode: {
    type: Boolean,
    default: false
  },
  sectionIndex: {
    type: Number,
    default: 0
  },
  totalSections: {
    type: Number,
    default: 1
  },
  availableWidgets: {
    type: Array,
    default: () => []
  }
})

const emit = defineEmits(['update:section', 'delete:section', 'update:widgets', 'add-widget', 'open-settings', 'move-section', 'remove-widget'])

const localWidgets = ref([...props.section.widgets])
const sectionContentRef = ref(null)
const sectionRef = ref(null)
const isDragOver = ref(false)
const clickedPosition = ref(null)

watch(() => props.section.widgets, (newWidgets) => {
  localWidgets.value = [...newWidgets]
}, { deep: true })

// Vue-grid-layout should automatically calculate height with auto-size="true"
// We just need to ensure the section-content properly contains it

const handleTitleUpdate = (newTitle) => {
  emit('update:section', props.section.id, { title: newTitle })
}

const handleExpandedUpdate = (expanded) => {
  emit('update:section', props.section.id, { expanded })
}

const handleDisplayModeUpdate = (displayMode) => {
  emit('update:section', props.section.id, { displayMode })
}

const isFocused = computed(() => (props.section.displayMode || 'detailed') === 'focused')
const automationsFooterEnabled = useAutomationsFooter()

// Section has widgets that show the automations footer (i.e. not automations-section widgets)
const sectionHasWidgetsWithFooter = computed(() => {
  const widgets = props.section.widgets || []
  return widgets.some((w) => {
    const widgetType = w.id?.includes('-') ? w.id.replace(/-\d+(-\d+)*$/, '') : w.id
    return widgetType && !AUTOMATIONS_SECTION_WIDGET_TYPES.includes(widgetType)
  })
})

// Focused mode: 112 base; +48 when section has widgets with footer. Detailed: rowHeight base; +48 when section has widgets with footer.
const sectionRowHeight = computed(() => {
  const showFooterSpace = automationsFooterEnabled.value && !props.editMode && sectionHasWidgetsWithFooter.value
  if (isFocused.value) {
    return showFooterSpace ? 160 : 112
  }
  return showFooterSpace ? props.rowHeight + 48 : props.rowHeight
})

const handleDelete = () => {
  emit('delete:section', props.section.id)
}

const handleWidgetsUpdate = (widgets) => {
  emit('update:widgets', { sectionId: props.section.id, widgets })
}

const handleDragOver = (event) => {
  if (!props.editMode) return
  event.preventDefault()
  event.dataTransfer.dropEffect = 'copy'
  isDragOver.value = true
}

const handleDragLeave = (event) => {
  // Only set to false if we're leaving the section content area
  if (!sectionContentRef.value?.contains(event.relatedTarget)) {
    isDragOver.value = false
  }
}

const handleDrop = async (event) => {
  if (!props.editMode) return
  event.preventDefault()
  isDragOver.value = false
  
  try {
    const data = JSON.parse(event.dataTransfer.getData('application/json'))
    const widgetTemplate = props.availableWidgets.find(w => w.id === data.widgetId)
    
    if (widgetTemplate) {
      const widgetData = {
        id: `${widgetTemplate.id}-${Date.now()}`,
        component: markRaw(widgetTemplate.component),
        colSpan: data.colSpan || widgetTemplate.defaultColSpan || 1,
        rowSpan: data.rowSpan || widgetTemplate.defaultRowSpan || 1,
        props: widgetTemplate.defaultProps || {}
      }
      
      // Use clicked position if available
      if (clickedPosition.value) {
        widgetData.x = clickedPosition.value.x
        widgetData.y = clickedPosition.value.y
        // Clear the position after use
        clickedPosition.value = null
      }
      
      emit('add-widget', {
        sectionId: props.section.id,
        widget: widgetData
      })
      
      // Wait for the widget to be added and DOM to update, then scroll to bottom of section
      await nextTick()
      // Use a small delay to ensure the grid layout has updated
      setTimeout(() => {
        if (sectionRef.value) {
          sectionRef.value.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'end',
            inline: 'nearest'
          })
        }
      }, 100)
    }
  } catch (error) {
    console.error('Error handling drop:', error)
  }
}

const handleEmptySectionClick = () => {
  if (!props.editMode) return
  // Emit event to open add widget panel
  emit('add-widget', { sectionId: props.section.id, openPanel: true })
}

const canMoveUp = computed(() => props.sectionIndex > 0)
const canMoveDown = computed(() => props.sectionIndex < props.totalSections - 1)

const handleMoveUp = () => {
  if (canMoveUp.value) {
    emit('move-section', props.sectionIndex, 'up')
  }
}

const handleMoveDown = () => {
  if (canMoveDown.value) {
    emit('move-section', props.sectionIndex, 'down')
  }
}

const handleRemoveWidget = (widgetId) => {
  emit('remove-widget', { sectionId: props.section.id, widgetId })
}

const handleOpenAddWidget = (data) => {
  // Store the clicked position if provided
  if (data?.position) {
    clickedPosition.value = data.position
  }
  emit('add-widget', { sectionId: props.section.id, openPanel: true })
}
</script>

<style scoped>
.dashboard-section {
  width: 100%;
  background: transparent;
  border-radius: 0;
  border: none;
  overflow: visible;
  position: relative;
  display: flex;
  flex-direction: column;
}

.section-content {
  padding: 0;
  margin: 0 20px;
  overflow: visible;
  width: calc(100% - 40px);
  position: relative;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  height: auto;
}

.section-content.edit-mode {
  border: 1px solid rgba(224, 224, 224, 1);
  border-radius: 12px;
  padding: 12px 16px 16px;
}

.section-header-wrapper {
  width: 100%;
  min-height: 40px;
  padding: 0;
  margin-bottom: 12px;
  border-bottom: none;
  display: flex;
  align-items: center;
  overflow: visible;
  position: relative;
  flex-shrink: 0;
  background: transparent;
  z-index: 10000;
}

.section-header-wrapper.edit-mode {
  margin-bottom: 12px;
  padding: 0;
  min-width: auto;
  align-items: center;
}

.section-body {
  padding: 0;
  margin-top: 0;
  min-height: 200px;
  overflow: visible;
  width: 100%;
  position: relative;
  flex-shrink: 0;
  display: block;
  height: auto;
}

.section-content.focused .section-body {
  min-height: 0;
}

.dashboard-section.focused {
  margin-bottom: -2px;
}

.section-content.drop-zone-active {
  background: rgba(35, 131, 226, 0.05);
  border: 2px dashed #2383E2;
  border-radius: 8px;
}

/* Ensure the grid layout and vue-grid-layout can properly calculate height */
.section-body :deep(.dashboard-grid) {
  height: auto;
  min-height: 0;
}

.section-body :deep(.vue-grid-layout) {
  min-height: 0;
}

.empty-section-message {
  text-align: center;
  padding: 60px 20px;
  color: #2383e2;
  font-size: 14px;
  border: 2px dashed #dee2e6;
  border-radius: 8px;
  background: #f8f9fa;
  transition: all 0.2s;
  cursor: pointer;
}

.empty-section-message:hover {
  border-color: #2383e2;
  background: rgba(35, 131, 226, 0.05);
  color: #2383e2;
}

.empty-section-message.drag-over {
  border-color: #2383e2;
  background: rgba(35, 131, 226, 0.05);
  color: #2383e2;
}

.empty-section-icon {
  margin-bottom: 12px;
  opacity: 1;
  color: #2383e2;
  transition: opacity 0.2s;
}

.empty-section-message:hover .empty-section-icon {
  opacity: 1;
  color: #2383e2;
}

@media (max-width: 768px) {
  .section-content {
    margin-left: 0;
    margin-right: 0;
    width: 100%;
  }
}
</style>
